import { useState, useEffect, useCallback, useRef } from "react";

/**
 * Voice Command Hook - Hands-free interaction for hospice caregivers
 * Uses Web Speech API for speech recognition and synthesis
 */

export function useVoiceCommands({
  onPainLevel,
  onMood,
  onSymptom,
  onCheckIn,
  onEmergency,
  onCallNurse,
  onNextVisit,
  onTalkToGoodwin,
  onVideoCall,
  onStressLevel,
  onSleepHours,
  onCancel,
  onConfirm,
  onUnknownCommand,
  enabled = true
}) {
  const [isListening, setIsListening] = useState(false);
  const [transcript, setTranscript] = useState("");
  const [lastCommand, setLastCommand] = useState(null);
  const [isSupported, setIsSupported] = useState(false);
  const [error, setError] = useState(null);
  const [isSpeaking, setIsSpeaking] = useState(false);
  
  const recognitionRef = useRef(null);
  const synthRef = useRef(null);
  const callbacksRef = useRef({
    onPainLevel, onMood, onSymptom, onCheckIn, onEmergency,
    onCallNurse, onNextVisit, onTalkToGoodwin, onVideoCall,
    onStressLevel, onSleepHours, onCancel, onConfirm, onUnknownCommand
  });

  // Update callbacks ref when they change
  useEffect(() => {
    callbacksRef.current = {
      onPainLevel, onMood, onSymptom, onCheckIn, onEmergency,
      onCallNurse, onNextVisit, onTalkToGoodwin, onVideoCall,
      onStressLevel, onSleepHours, onCancel, onConfirm, onUnknownCommand
    };
  }, [onPainLevel, onMood, onSymptom, onCheckIn, onEmergency,
      onCallNurse, onNextVisit, onTalkToGoodwin, onVideoCall,
      onStressLevel, onSleepHours, onCancel, onConfirm, onUnknownCommand]);

  // Process voice command
  const processCommand = useCallback((text) => {
    const lowerText = text.toLowerCase();
    const callbacks = callbacksRef.current;
    let command = null;
    
    // Emergency (highest priority)
    if (/emergency|help|911|urgent/.test(lowerText)) {
      command = { type: "emergency", text };
      callbacks.onEmergency?.();
    }
    // Cancel
    else if (/cancel|stop|never ?mind/.test(lowerText)) {
      command = { type: "cancel", text };
      callbacks.onCancel?.();
    }
    // Confirm
    else if (/yes|confirm|submit|send|okay|ok|sure/.test(lowerText)) {
      command = { type: "confirm", text };
      callbacks.onConfirm?.();
    }
    // Pain level
    else if (/pain.*(\d+)|(\d+).*pain/.test(lowerText)) {
      const match = lowerText.match(/(\d+)/);
      const level = match ? parseInt(match[1]) : null;
      if (level !== null && level >= 0 && level <= 10) {
        command = { type: "pain_level", value: level, text };
        callbacks.onPainLevel?.(level);
      }
    }
    // Mood
    else if (/mood|feeling|i feel|i am|i'm/.test(lowerText)) {
      const moodMatch = lowerText.match(/great|good|okay|ok|sad|anxious|depressed|terrible|bad|low/);
      if (moodMatch) {
        const moodMap = {
          great: "great", good: "good", okay: "okay", ok: "okay",
          sad: "sad", anxious: "anxious", depressed: "depressed",
          terrible: "depressed", bad: "sad", low: "depressed"
        };
        const mood = moodMap[moodMatch[0]];
        command = { type: "mood", value: mood, text };
        callbacks.onMood?.(mood);
      }
    }
    // Symptom
    else if (/have|having|symptom/.test(lowerText)) {
      const symptomMatch = lowerText.match(/nausea|fatigue|tired|breathing|breath|confusion|confused|fever/);
      if (symptomMatch) {
        const symptomMap = {
          nausea: "nausea", fatigue: "fatigue", tired: "fatigue",
          breathing: "breathing", breath: "breathing",
          confusion: "confusion", confused: "confusion", fever: "fever"
        };
        const symptom = symptomMap[symptomMatch[0]];
        command = { type: "symptom", value: symptom, text };
        callbacks.onSymptom?.(symptom);
      }
    }
    // Stress level
    else if (/stress.*(\d+)|(\d+).*stress/.test(lowerText)) {
      const match = lowerText.match(/(\d+)/);
      const level = match ? parseInt(match[1]) : null;
      if (level !== null && level >= 0 && level <= 10) {
        command = { type: "stress_level", value: level, text };
        callbacks.onStressLevel?.(level);
      }
    }
    // Sleep hours
    else if (/slept?|sleep/.test(lowerText)) {
      const match = lowerText.match(/(\d+(?:\.\d+)?)/);
      const hours = match ? parseFloat(match[1]) : null;
      if (hours !== null && hours >= 0 && hours <= 24) {
        command = { type: "sleep_hours", value: hours, text };
        callbacks.onSleepHours?.(hours);
      }
    }
    // Check in
    else if (/check in|checkin|daily check/.test(lowerText)) {
      command = { type: "check_in", text };
      callbacks.onCheckIn?.();
    }
    // Call nurse
    else if (/call.*nurse|contact.*nurse|phone.*nurse/.test(lowerText)) {
      command = { type: "call_nurse", text };
      callbacks.onCallNurse?.();
    }
    // Next visit
    else if (/next visit|upcoming visit|schedule/.test(lowerText)) {
      command = { type: "next_visit", text };
      callbacks.onNextVisit?.();
    }
    // Talk to Goodwin
    else if (/goodwin|companion|assistant/.test(lowerText)) {
      command = { type: "talk_goodwin", text };
      callbacks.onTalkToGoodwin?.();
    }
    // Video call
    else if (/video call|telehealth/.test(lowerText)) {
      command = { type: "video_call", text };
      callbacks.onVideoCall?.();
    }
    // Unknown
    else {
      command = { type: "unknown", text };
      callbacks.onUnknownCommand?.(text);
    }
    
    setLastCommand(command);
    return command;
  }, []);

  // Initialize speech recognition
  useEffect(() => {
    if (typeof window === "undefined") return;
    
    const SpeechRecognition = window.SpeechRecognition || window.webkitSpeechRecognition;
    
    if (SpeechRecognition) {
      setIsSupported(true);
      const recognition = new SpeechRecognition();
      recognition.continuous = true;
      recognition.interimResults = true;
      recognition.lang = "en-US";
      
      recognition.onresult = (event) => {
        let finalTranscript = "";
        let interimTranscript = "";
        
        for (let i = event.resultIndex; i < event.results.length; i++) {
          const t = event.results[i][0].transcript;
          if (event.results[i].isFinal) {
            finalTranscript += t;
          } else {
            interimTranscript += t;
          }
        }
        
        setTranscript(interimTranscript || finalTranscript);
        
        if (finalTranscript) {
          processCommand(finalTranscript.trim());
        }
      };
      
      recognition.onerror = (event) => {
        if (event.error !== "no-speech") {
          setError(event.error);
        }
      };
      
      recognitionRef.current = recognition;
    }
    
    if (window.speechSynthesis) {
      synthRef.current = window.speechSynthesis;
    }
    
    return () => {
      if (recognitionRef.current) {
        try {
          recognitionRef.current.stop();
        } catch (e) {
          // Ignore
        }
      }
    };
  }, [processCommand]);

  // Start listening
  const startListening = useCallback(() => {
    if (!recognitionRef.current || !enabled) return;
    
    setError(null);
    setTranscript("");
    setIsListening(true);
    
    try {
      recognitionRef.current.start();
    } catch (e) {
      // Already started
    }
  }, [enabled]);

  // Stop listening
  const stopListening = useCallback(() => {
    setIsListening(false);
    if (recognitionRef.current) {
      try {
        recognitionRef.current.stop();
      } catch (e) {
        // Ignore
      }
    }
  }, []);

  // Toggle listening
  const toggleListening = useCallback(() => {
    if (isListening) {
      stopListening();
    } else {
      startListening();
    }
  }, [isListening, startListening, stopListening]);

  // Text-to-speech
  const speak = useCallback((text) => {
    if (!synthRef.current) return;
    
    synthRef.current.cancel();
    
    const utterance = new SpeechSynthesisUtterance(text);
    utterance.rate = 0.9;
    utterance.onstart = () => setIsSpeaking(true);
    utterance.onend = () => setIsSpeaking(false);
    utterance.onerror = () => setIsSpeaking(false);
    
    synthRef.current.speak(utterance);
  }, []);

  // Stop speaking
  const stopSpeaking = useCallback(() => {
    if (synthRef.current) {
      synthRef.current.cancel();
      setIsSpeaking(false);
    }
  }, []);

  return {
    isListening,
    isSupported,
    isSpeaking,
    transcript,
    lastCommand,
    error,
    startListening,
    stopListening,
    toggleListening,
    speak,
    stopSpeaking,
    processCommand
  };
}

export default useVoiceCommands;
